/**
 * This file was originally ported from WinJS.
 */

import * as Events from '../events/Events';

export interface IThenable<T> {
    then<U>(onComplete?: OnComplete<T, U> | null | undefined, onError?: OnError<U> | null | undefined): IThenable<U>;
    then<U, V>(onComplete?: OnComplete<T, U> | null | undefined, onError?: OnError<V> | null | undefined): IThenable<U>;
}

/**
 * Contract for the function to resolve a promise wrapping an async operation.
 */
export type Resolve<T> = (result?: T | IThenable<T> | ICancelablePromise<T> | null | undefined) => void;
/**
 * Contract for the function to reject a promise wrapping an async operation.
 */
export type Reject = (reason?: any) => void;

/**
 * Interface for a cancelable extension of Promise.
 * This interface extends the ES6 Promise interface, ensuring compatibility between
 * the provided implementation of CancelablePromise and other code which expects ES6 Promises.
 *
 * @export
 * @interface ICancelablePromise
 * @extends {Promise<T>}
 * @template T
 */
export declare interface ICancelablePromise<T> extends Promise<T> {
    /**
     * Default overload which asserts that cancelable promises always beget new cancelable promises.
     *
     * @template U
     * @param {OnComplete<T, U>} [onComplete]
     * @param {OnError<U>} [onError]
     * @returns {ICancelablePromise<U>}
     * @memberof ICancelablePromise
     */
    then<U>(onComplete?: OnComplete<T, U> | null | undefined, onError?: OnError<U> | null | undefined): ICancelablePromise<U>;
    then<U, V>(onComplete?: OnComplete<T, U> | null | undefined, onError?: OnError<V> | null | undefined): ICancelablePromise<U>;
    /**
     * Default overload which asserts that cancelable promises always beget new cancelable promises.
     *
     * @template U
     * @param {OnError<U>} [onrejected]
     * @returns {ICancelablePromise<U>}
     * @memberof ICancelablePromise
     */
    catch<U>(onrejected?: OnError<U> | null | undefined): ICancelablePromise<T | U>;
    /**
     * Chains additional work to the current promise, without allowing for additional chaining.
     * By default, any errors not explicitly handled will be thrown into the event loop.
     *
     * @param {(value: T) => any} [onComplete]
     * @param {(error: any) => any} [onError]
     * @memberof ICancelablePromise
     */
    done(onComplete?: (value: T) => any, onError?: (error: any) => any): void;
    /**
     * Marks the promise as canceled, which attempts to terminate any active ancestor promises
     * of the current promise, and fails with an error marked for cancellation.
     *
     * @memberof ICancelablePromise
     */
    cancel(): void;
}

/**
 * Constructor type for cancelable promises which extends the ES6 Promise constructor to serve as
 * a polyfill if needed, and provides static methods to work with cancelable promises.
 */
export type ICancelablePromiseConstructor = {
    /**
     * Constructs a new cancelable promise.
     * The `executor` callback is invoked immediately to let the async operation
     * access the `resolve` and `reject` continuation functions.
     * The `onCancel` callback will be invoked if the promise is marked for cancellation,
     * allowing the async operation to react immediately.
     */
    new <T>(executor?: (resolve: Resolve<T>, reject: Reject) => void, onCancel?: OnCancel): ICancelablePromise<T>;

    /**
     * Produces a promise which indicates which of the given input promises completed first, along with its associated key.
     *
     * @template T
     * @param {(ICancelablePromise<T>[] | { [key: string]: ICancelablePromise<T> })} values
     * @returns {ICancelablePromise<{ key: string; value: ICancelablePromise<T>; }>}
     */
    any<T>(values: ICancelablePromise<T>[] | { [key: string]: ICancelablePromise<T> }): ICancelablePromise<{ key: string; value: ICancelablePromise<T>; }>;

    /**
     * Wraps the input value if it is not a cancelable promise, otherwise reusing an existing cancelable promise if provided.
     *
     * @template T
     * @param {(T | IThenable<T> | ICancelablePromise<T>)} [value]
     * @returns {ICancelablePromise<T>}
     */
    as<T>(value?: T | IThenable<T> | ICancelablePromise<T>): ICancelablePromise<T>;

    /**
     * Determines whether or not the given input is a cancelable promise.
     * Note that mere `Promise` implementations which are not cancelable will cause this method to return `false`.
     * Use `isPromise` to test for normal `Promise` implementations.
     *
     * @template T
     * @param {(T | IThenable<T> | ICancelablePromise<T>)} value
     * @returns {value is ICancelablePromise<T>}
     */
    is<T>(value: T | IThenable<T> | ICancelablePromise<T>): value is ICancelablePromise<T>;
    /**
     * Determines whether or not the given input satisifes the contract of `Promise`.
     *
     * @template T
     * @param {(T | IThenable<T>)} value
     * @returns {value is IThenable<T>}
     */
    isPromise<T>(value: T | IThenable<T>): value is IThenable<T>

    /**
     * Determines whether or not the given input is an error representing cancellation.
     *
     * @param {*} e
     * @returns {boolean}
     */
    isCanceled(e: any): boolean;

    /**
     * Produces a promise which completes with the output values of all input promises.
     *
     * @template T
     * @param {((T | ICancelablePromise<T>)[])} values
     * @returns {ICancelablePromise<T[]>}
     */
    all<T>(values: (T | IThenable<T> | ICancelablePromise<T>)[]): ICancelablePromise<T[]>;
    all<T>(values: MaybePromisified<T>): ICancelablePromise<T>;

    /**
     * Produces a chained promise from the given input, wrapping it with cancellation support if necessary.
     *
     * @template T
     * @template U
     * @param {(T | IThenable<T> | ICancelablePromise<T>)} value
     * @param {OnComplete<T, U>} onComplete
     * @param {OnError<U>} onError
     * @returns {ICancelablePromise<U>}
     */
    then<T, U>(value: T | IThenable<T> | ICancelablePromise<T>, onComplete: OnComplete<T, U>, onError: OnError<U>): ICancelablePromise<U>;

    /**
     * Produces a series of chained promises which represent the given continuation from each source promise.
     *
     * @template T
     * @template U
     * @param {ICancelablePromise<T>[]} values
     * @param {OnComplete<T, U>} onComplete
     * @param {OnError<U>} [onError]
     * @returns {ICancelablePromise<U[]>}
     */
    thenEach<T, U>(
        values: ICancelablePromise<T>[],
        onComplete: OnComplete<T, U>,
        onError?: OnError<U>): ICancelablePromise<U[]>;
    thenEach<T, U>(
        values: { [key: string]: ICancelablePromise<T> },
        onComplete: OnComplete<T, U>,
        onError?: OnError<U>): ICancelablePromise<{ [keys: string]: U }>;

    /**
     * Produces a promise representing the result of each promise chained to the previous promise.
     *
     * @template T
     * @param {(() => (IThenable<T>))[]} tasks
     * @returns {ICancelablePromise<T>}
     */
    serial<T>(tasks: (() => (IThenable<T>))[]): ICancelablePromise<T>;
    serial(tasks: (() => (IThenable<any>))[]): ICancelablePromise<any>;

    /**
     * Produces a promise which represents a delay in time, optionally
     * waiting for completion of a previous promise first.
     *
     * @template T
     * @param {number} time
     * @param {ICancelablePromise<T>} [promise]
     * @returns {ICancelablePromise<T>}
     */
    timeout<T>(time: number, promise?: ICancelablePromise<T>): ICancelablePromise<T>;

    /**
     * Creates a new cancelable promise which completes with the given result,
     * waiting on a wrapped promise if necessary.
     *
     * @template T
     * @param {(T | IThenable<T> | ICancelablePromise<T>)} [value]
     * @returns {ICancelablePromise<T>}
     */
    wrap<T>(value?: T | IThenable<T> | ICancelablePromise<T>): ICancelablePromise<T>;

    /**
     * Creates a new cancelable promise which has already failed with the given error.
     *
     * @param {*} [error]
     * @returns {ICancelablePromise<any>}
     */
    wrapError(error?: any): ICancelablePromise<any>;

    /**
     * Make a new promise from the thenable.
     * A thenable is promise-like in as far as it has a "then" method.
     */
    resolve<T>(value?: T | IThenable<T>): ICancelablePromise<T>;

    /**
     * Make a promise that rejects to obj. For consistency and debugging (eg stack traces), obj should be an instanceof Error
     */
    reject(error: any): ICancelablePromise<any>;
    reject<T>(error: T): ICancelablePromise<T>;
} & {
        [K in keyof typeof Promise]: (typeof Promise)[K];
    };

// tslint:disable-next-line:interface-name
export interface OnComplete<T, U> {
    (result: T): void | U | IThenable<U> | ICancelablePromise<U>;
}

// tslint:disable-next-line:interface-name
export interface OnError<U> {
    (error?: any): void | U | IThenable<U> | ICancelablePromise<U>;
    handlesOnError?: boolean;
}

/**
 * Contract for a cancellation handler for a promise.
 */
export type OnCancel = () => any;

function doneHandler(value: any) {
    /* tslint:disable-next-line:ban-native-functions */
    window.setTimeout(() => {
        Events.raise(CancelablePromiseImplementation, errorET, { error: value } as IErrorDetails);
    }, 0);
}

const errorET = "error";
const canceledName = "Canceled";

interface IErrorDetails {
    exception: any;
    error: any;
    promise: any;
    id: number;
    handler: OnError<any>;
    parent: any;
}

interface IErrorContext {
    _isException?: boolean;
    _errorId?: number;
}

type ErrorDetailsGenerator = (promise: IThenable<any>, errorValue: any, context?: IErrorContext, handler?: OnError<any>) => IErrorDetails;

interface IListener<T, U> {
    c?: OnComplete<T, U>;
    e?: OnError<U>;
    promise?: CancelablePromiseImplementation<any>;
}

//
// Global error counter, for each error which enters the system we increment this once and then
// the error number travels with the error as it traverses the tree of potential handlers.
//
// When someone has registered to be told about error [using EventGroup.on(Promise, 'error')] promises
// which are in error will get tagged with a ._errorId field. This tagged field is the
// contract by which nested promises with errors will be identified as chaining for the
// purposes of the callonerror semantics. If a nested promise in error is encountered without
// a ._errorId it will be assumed to be foreign and treated as an interop boundary and
// a new error id will be minted.
//
let error_number = 1;

//
// The state machine has a interesting hiccup in it with regards to notification, in order
// to flatten out notification and avoid recursion for synchronous completion we have an
// explicit set of *_notify states which are responsible for notifying their entire tree
// of children. They can do this because they know that immediate children are always
// ThenPromise instances and we can therefore reach into their state to access the
// _listeners collection.
//
// So, what happens is that a Promise will be fulfilled through the _completed or _error
// messages at which point it will enter a *_notify state and be responsible for to move
// its children into an (as appropriate) success or error state and also notify that child's
// listeners of the state transition, until leaf notes are reached.
//

interface IState {
    name: string;
    enter<T>(promise: CancelablePromiseImplementation<T>): void;
    cancel<T>(promise: CancelablePromiseImplementation<T>): void;
    done<T>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<{}, void>, onError: OnError<void>): void;
    then<T, U>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<T, U>, onError: OnError<U>): ICancelablePromise<U>;
    _completed<T>(promise: CancelablePromiseImplementation<T>, value: T | IThenable<T> | ICancelablePromise<T>): void;
    _error<T>(promise: CancelablePromiseImplementation<T>, value: {} | IThenable<{}> | ICancelablePromise<{}>, onerrorDetails: ErrorDetailsGenerator, context?: IErrorContext): void;
    _notify<T>(promise: CancelablePromiseImplementation<T>, queue: CancelablePromiseImplementation<{}>[]): void;
    _setCompleteValue<T>(promise: CancelablePromiseImplementation<T>, value: T): void;
    _setErrorValue<T>(promise: CancelablePromiseImplementation<T>, value: {}, onerrorDetails: ErrorDetailsGenerator, context?: IErrorContext): void;
}

let state_created: IState;              // -> working
let state_working: IState;              // -> error | error_notify | success | success_notify | canceled | waiting
let state_waiting: IState;              // -> error | error_notify | success | success_notify | waiting_canceled
let state_waiting_canceled: IState;     // -> error | error_notify | success | success_notify | canceling
let state_canceled: IState;             // -> error | error_notify | success | success_notify | canceling
let state_canceling: IState;            // -> error_notify
let state_success_notify: IState;       // -> success
let state_success: IState;              // -> .
let state_error_notify: IState;         // -> error
let state_error: IState;                // -> .

// Noop function, used in the various states to indicate that they don't support a given
// message. Named with the somewhat cute name '_' because it reads really well in the states.

function _(): any { /* No-op */ }

// Initial state
//
state_created = {
    name: "created",
    enter: function (promise: CancelablePromiseImplementation<{}>) {
        promise._setState(state_working);
    },
    cancel: _,
    done: _,
    then: _,
    _completed: _,
    _error: _,
    _notify: _,
    _setCompleteValue: _,
    _setErrorValue: _
};

// Ready state, waiting for a message (completed/error), able to be canceled
//
state_working = {
    name: "working",
    enter: _,
    cancel: function <T>(promise: CancelablePromiseImplementation<T>) {
        promise._setState(state_canceled);
    },
    done: done,
    then: then,
    _completed: completed,
    _error: error,
    _notify: _,
    _setCompleteValue: setCompleteValue,
    _setErrorValue: setErrorValue
};

// Waiting state, if a promise is completed with a value which is itself a promise
// (has a then() method) it signs up to be informed when that child promise is
// fulfilled at which point it will be fulfilled with that value.
//
state_waiting = {
    name: "waiting",
    enter: function <T>(promise: CancelablePromiseImplementation<T>) {
        let waitedUpon = <CancelablePromiseImplementation<T>>promise._value;
        // We can special case our own intermediate promises which are not in a
        //  terminal state by just pushing this promise as a listener without
        //  having to create new indirection functions
        if (waitedUpon instanceof ThenPromise &&
            waitedUpon._state !== state_error &&
            waitedUpon._state !== state_success) {
            pushListener(waitedUpon, { promise: promise });
        } else {
            let error: OnError<void> = function (value: {}) {
                if (waitedUpon._errorId) {
                    promise._chainedError(value, waitedUpon);
                } else {
                    // Because this is an interop boundary we want to indicate that this
                    //  error has been handled by the promise infrastructure before we
                    //  begin a new handling chain.
                    //
                    callonerror(promise, value, detailsForHandledError, waitedUpon, error);
                    promise._error(value);
                }
            };
            error.handlesOnError = true;
            waitedUpon.then(
                promise._completed.bind(promise),
                error
            );
        }
    },
    cancel: function <T>(promise: CancelablePromiseImplementation<T>) {
        promise._setState(state_waiting_canceled);
    },
    done: done,
    then: then,
    _completed: completed,
    _error: error,
    _notify: _,
    _setCompleteValue: setCompleteValue,
    _setErrorValue: setErrorValue
};

// Waiting canceled state, when a promise has been in a waiting state and receives a
// request to cancel its pending work it will forward that request to the child promise
// and then waits to be informed of the result. This promise moves itself into the
// canceling state but understands that the child promise may instead push it to a
// different state.
//
state_waiting_canceled = {
    name: "waiting_canceled",
    enter: function (promise: CancelablePromiseImplementation<{}>) {
        // Initiate a transition to canceling. Triggering a cancel on the promise
        // that we are waiting upon may result in a different state transition
        // before the state machine pump runs again.
        promise._setState(state_canceling);
        let waitedUpon = promise._value;

        if (CancelablePromiseImplementation.is(waitedUpon)) {
            waitedUpon.cancel();
        }
    },
    cancel: _,
    done: done,
    then: then,
    _completed: completed,
    _error: error,
    _notify: _,
    _setCompleteValue: setCompleteValue,
    _setErrorValue: setErrorValue
};

// Canceled state, moves to the canceling state and then tells the promise to do
// whatever it might need to do on cancelation.
//
state_canceled = {
    name: "canceled",
    enter: function (promise: CancelablePromiseImplementation<{}>) {
        // Initiate a transition to canceling. The _cancelAction may change the state
        // before the state machine pump runs again.
        promise._setState(state_canceling);
        promise._cancelAction();
    },
    cancel: _,
    done: done,
    then: then,
    _completed: completed,
    _error: error,
    _notify: _,
    _setCompleteValue: setCompleteValue,
    _setErrorValue: setErrorValue
};

// Canceling state, commits to the promise moving to an error state with an error
// object whose 'name' and 'message' properties contain the string "Canceled"
//
state_canceling = {
    name: "canceling",
    enter: function (promise: CancelablePromiseImplementation<{}>) {
        let error = new Error(canceledName);
        error.name = error.message;
        error["_handled"] = true;
        promise._value = error;
        promise._setState(state_error_notify);
    },
    cancel: _,
    done: _,
    then: _,
    _completed: _,
    _error: _,
    _notify: _,
    _setCompleteValue: _,
    _setErrorValue: _
};

// Success notify state, moves a promise to the success state and notifies all children
//
state_success_notify = {
    name: "complete_notify",
    enter: function (promise: CancelablePromiseImplementation<{}>) {
        if (promise._listeners) {
            let queue = [promise];
            let p;
            while (queue.length) {
                p = queue.shift();
                p._state._notify(p, queue);
            }
        }
        promise._setState(state_success);
    },
    cancel: _,
    done: <T>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<{}, void>, onError: OnError<void>) => CompletePromise.prototype.done.call(promise, onComplete, onError),
    then: <T, U>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<T, U>, onError: OnError<U>) => CompletePromise.prototype.then.call(promise, onComplete, onError),
    _completed: _,
    _error: _,
    _notify: notifySuccess,
    _setCompleteValue: _,
    _setErrorValue: _
};

// Success state, moves a promise to the success state and does NOT notify any children.
// Some upstream promise is owning the notification pass.
//
state_success = {
    name: "success",
    enter: function (promise: CancelablePromiseImplementation<{}>) {
        promise._cleanupAction();
    },
    cancel: _,
    done: <T>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<{}, void>, onError: OnError<void>) => CompletePromise.prototype.done.call(promise, onComplete, onError),
    then: <T, U>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<T, U>, onError: OnError<U>) => CompletePromise.prototype.then.call(promise, onComplete, onError),
    _completed: _,
    _error: _,
    _notify: notifySuccess,
    _setCompleteValue: _,
    _setErrorValue: _
};

// Error notify state, moves a promise to the error state and notifies all children
//
state_error_notify = {
    name: "error_notify",
    enter: function (promise: CancelablePromiseImplementation<{}>) {
        if (promise._listeners) {
            let queue = [promise];
            let p;
            while (queue.length) {
                p = queue.shift();
                p._state._notify(p, queue);
            }
        }
        promise._setState(state_error);
    },
    cancel: _,
    done: <T>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<{}, void>, onError: OnError<void>) => ErrorPromise.prototype.done.call(promise, onComplete, onError),
    then: <T, U>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<T, U>, onError: OnError<U>) => ErrorPromise.prototype.then.call(promise, onComplete, onError),
    _completed: _,
    _error: _,
    _notify: notifyError,
    _setCompleteValue: _,
    _setErrorValue: _
};

// Error state, moves a promise to the error state and does NOT notify any children.
// Some upstream promise is owning the notification pass.
//
state_error = {
    name: "error",
    enter: function (promise: CancelablePromiseImplementation<{}>) {
        promise._cleanupAction();
    },
    cancel: _,
    done: <T>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<{}, void>, onError: OnError<void>) => ErrorPromise.prototype.done.call(promise, onComplete, onError),
    then: <T, U>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<T, U>, onError: OnError<U>) => ErrorPromise.prototype.then.call(promise, onComplete, onError),
    _completed: _,
    _error: _,
    _notify: notifyError,
    _setCompleteValue: _,
    _setErrorValue: _
};

//
// The statemachine implementation follows a very particular pattern, the states are specified
// as static stateless bags of functions which are then indirected through the state machine
// instance (a Promise). As such all of the functions on each state have the promise instance
// passed to them explicitly as a parameter and the Promise instance members do a little
// dance where they indirect through the state and insert themselves in the argument list.
//
// We could instead call directly through the promise states however then every caller
// would have to remember to do things like pumping the state machine to catch state transitions.
//

//
// Implementations of shared state machine code.
//

function completed(promise: CancelablePromiseImplementation<{}>, value: {} | IThenable<{}> | ICancelablePromise<{}>) {
    promise._value = value;
    promise._setState(CancelablePromiseImplementation.isPromise(value) ? state_waiting : state_success_notify);
}

function createErrorDetails(exception: any, error: any, promise: CancelablePromiseImplementation<{}>, id: number, parent?: IErrorContext, handler?: OnError<any>): IErrorDetails {
    return {
        exception: exception,
        error: error,
        promise: promise,
        handler: handler,
        id: id,
        parent: parent
    };
}

function detailsForHandledError(promise: CancelablePromiseImplementation<{}>, errorValue: any, context: IErrorContext, handler: OnError<any>) {
    let exception = context._isException;
    let errorId = context._errorId;
    return createErrorDetails(
        exception ? errorValue : null,
        exception ? null : errorValue,
        promise,
        errorId,
        context,
        handler
    );
}

function detailsForChainedError(promise: CancelablePromiseImplementation<{}>, errorValue: any, context: IErrorContext) {
    let exception = context._isException;
    let errorId = context._errorId;
    setErrorInfo(promise, errorId, exception);
    return createErrorDetails(
        exception ? errorValue : null,
        exception ? null : errorValue,
        promise,
        errorId,
        context
    );
}

function detailsForError(promise: CancelablePromiseImplementation<{}>, errorValue: any) {
    let errorId = ++error_number;
    setErrorInfo(promise, errorId);
    return createErrorDetails(
        null,
        errorValue,
        promise,
        errorId
    );
}

function detailsForException(promise: CancelablePromiseImplementation<{}>, exceptionValue: any) {
    let errorId = ++error_number;
    setErrorInfo(promise, errorId, true);
    return createErrorDetails(
        exceptionValue,
        null,
        promise,
        errorId
    );
}

function done<T>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<T, void>, onError: OnError<void>) {
    pushListener(promise, { c: onComplete, e: onError });
}

function error<T>(promise: CancelablePromiseImplementation<T>, value: T | IThenable<T> | ICancelablePromise<T>, onerrorDetails: ErrorDetailsGenerator, context: IErrorContext) {
    promise._value = value;
    callonerror(promise, value, onerrorDetails, context);
    promise._setState(state_error_notify);
}

function notifySuccess<T>(promise: CancelablePromiseImplementation<T>, queue: CancelablePromiseImplementation<T>[]) {
    let value = <T>promise._value;
    let listeners = promise._listeners;
    if (!listeners) {
        return;
    }
    promise._listeners = null;
    let i: number;
    let len: number;
    for (i = 0, len = listeners.length; i < len; i++) {
        let listener = listeners[i];
        let onComplete = listener.c;
        let target = listener.promise;

        if (target) {
            try {
                target._setCompleteValue(onComplete ? onComplete(value) : value);
            } catch (ex) {
                target._setExceptionValue(ex);
            }

            if (target._state !== state_waiting && target._listeners) {
                queue.push(target);
            }
        } else {
            CompletePromise.prototype.done.call(promise, onComplete);
        }
    }
}

function notifyError<T>(promise: CancelablePromiseImplementation<T>, queue: CancelablePromiseImplementation<any>[]) {
    let value = promise._value;
    let listeners = promise._listeners;
    if (!listeners) {
        return;
    }
    promise._listeners = null;
    let i: number;
    let len: number;
    for (i = 0, len = listeners.length; i < len; i++) {
        let listener = listeners[i];
        let onError = listener.e;
        let target = listener.promise;

        if (target) {
            try {
                if (onError) {
                    if (!onError.handlesOnError) {
                        callonerror(target, value, detailsForHandledError, promise, onError);
                    }
                    target._setCompleteValue(onError(value));
                } else {
                    target._setChainedErrorValue(value, promise);
                }
            } catch (ex) {
                target._setExceptionValue(ex);
            }

            if (target._state !== state_waiting && target._listeners) {
                queue.push(target);
            }
        } else {
            ErrorPromise.prototype.done.call(promise, null, onError);
        }
    }
}

function callonerror(promise: IThenable<{}>, value: {}, onerrorDetailsGenerator: ErrorDetailsGenerator, context?: IErrorContext, handler?: OnError<any>) {
    if (value instanceof Error && value.message === canceledName) {
        return;
    }

    Events.raise(CancelablePromiseImplementation, errorET, onerrorDetailsGenerator(promise, value, context, handler));
}

function pushListener<T, U>(promise: CancelablePromiseImplementation<T>, listener: IListener<T, U>) {
    let listeners = promise._listeners;
    if (listeners) {
        listeners.push(listener);
    } else {
        listeners = [listener];
    }
    promise._listeners = listeners;
}

// The difference beween setCompleteValue()/setErrorValue() and complete()/error() is that setXXXValue() moves
// a promise directly to the success/error state without starting another notification pass (because one
// is already ongoing).
function setErrorInfo(promise: CancelablePromiseImplementation<{}>, errorId: number, isException?: boolean) {
    promise._isException = isException || false;
    promise._errorId = errorId;
}

function setErrorValue(promise: CancelablePromiseImplementation<{}>, value: {} | IThenable<{}> | ICancelablePromise<{}>, onerrorDetails: ErrorDetailsGenerator, context: IErrorContext) {
    promise._value = value;
    callonerror(promise, value, onerrorDetails, context);
    promise._setState(state_error);
}

function setCompleteValue(promise: CancelablePromiseImplementation<{}>, value: {} | IThenable<{}> | ICancelablePromise<{}>) {
    let targetState;
    if (CancelablePromiseImplementation.isPromise(value)) {
        targetState = state_waiting;
    } else {
        targetState = state_success;
    }
    promise._value = value;
    promise._setState(targetState);
}

function then<T, U>(promise: CancelablePromiseImplementation<T>, onComplete: OnComplete<T, U>, onError: OnError<U>): ICancelablePromise<U> {
    let result = new ThenPromise<U>(promise);
    pushListener(promise, { promise: result, c: onComplete, e: onError });
    return result;
}

//
// Slim promise implementations for already completed promises, these are created
// under the hood on synchronous completion paths as well as by Promise.wrap
// and Promise.wrapError.
//

class ErrorPromise<T> implements IErrorContext, ICancelablePromise<T> {
    public _value: {};
    public _isException: boolean;
    public _errorId: number;

    constructor(value: {}, errorFunc: typeof detailsForError = detailsForError) {
        this._value = value;
        callonerror(this, value, errorFunc);
    }

    public cancel() {
        /* No-op */
    }

    public done(unused: OnComplete<T, void>, onError: OnError<void>) {
        let value = this._value;
        if (onError) {
            try {
                if (!onError.handlesOnError) {
                    callonerror(null, value, detailsForHandledError, this, onError);
                }
                let result = onError(value);
                if (CancelablePromiseImplementation.is(result)) {
                    // If a promise is returned we need to wait on it.
                    result.done();
                }
                return;
            } catch (ex) {
                value = ex;
            }
        }
        if (value instanceof Error && value.message === canceledName) {
            // suppress cancel
            return;
        }
        // force the exception to be thrown asyncronously to avoid any try/catch blocks
        //
        doneHandler(value);
    }

    public then<U>(unused: OnComplete<T, U>): ICancelablePromise<T>;
    public then<U>(unused: OnComplete<T, U>, onError: OnError<U>): ICancelablePromise<U>;
    public then<U, V>(unused: OnComplete<T, U>, onError: OnError<V>): ICancelablePromise<U>;
    public then<U>(unused: OnComplete<T, U>, onError?: OnError<U>): ICancelablePromise<T | U> {
        // If the promise is already in a error state and no error handler is provided
        // we optimize by simply returning the promise instead of creating a new one.
        //
        if (!onError) { return this; }
        let result;
        let value = this._value;
        try {
            if (!onError.handlesOnError) {
                callonerror(null, value, detailsForHandledError, this, onError);
            }
            result = new CompletePromise(onError(value));
        } catch (ex) {
            // If the value throw from the error handler is the same as the value
            // provided to the error handler then there is no need for a new promise.
            //
            if (ex === value) {
                result = this;
            } else {
                result = new ExceptionPromise(ex);
            }
        }
        return result;
    }

    public catch<U>(onError: OnError<U>) {
        return this.then(undefined, onError);
    }
}

class ExceptionPromise<T> extends ErrorPromise<T> {
    constructor(value: any) {
        super(value, detailsForException);
    }
}

class CompletePromise<T> implements ICancelablePromise<T> {
    public _value: void | T | ICancelablePromise<T> | IThenable<T>;

    constructor(value: void | T | ICancelablePromise<T> | IThenable<T>) {
        if (CancelablePromiseImplementation.isPromise(value)) {
            let result = new ThenPromise<T>(undefined);
            result._setCompleteValue(value);
            return result;
        }
        this._value = value;
    }

    public cancel() {
        /* No-op */
    }
    public done(onComplete: OnComplete<T, void>) {
        if (!onComplete) { return; }
        try {
            let result = onComplete(<T>this._value);
            if (CancelablePromiseImplementation.is(result)) {
                result.done();
            }
        } catch (ex) {
            // force the exception to be thrown asynchronously to avoid any try/catch blocks
            doneHandler(ex);
        }
    }
    public then<U>(onComplete: OnComplete<T, U>): CancelablePromise<U> {
        let resultPromise: ICancelablePromise<U>;
        try {
            // If the value returned from the completion handler is the same as the value
            // provided to the completion handler then there is no need for a new promise.
            //
            let newValue = onComplete ? onComplete(<T>this._value) : this._value;
            resultPromise = this._isSameValue(newValue) ? this : new CompletePromise<U>(<U>newValue);
        } catch (ex) {
            resultPromise = new ExceptionPromise(ex);
        }
        return resultPromise;
    }

    public catch<U>(onError: OnError<U>): ICancelablePromise<U> {
        return this.then(undefined);
    }

    public _isSameValue<U>(value: void | T | IThenable<T> | ICancelablePromise<T> | U | IThenable<U> | ICancelablePromise<U>): this is ICancelablePromise<U> {
        return value === this._value;
    }
}

function timeout(timeoutMS: number) {
    let id: number;
    return new CancelablePromiseImplementation<void>(
        function (c: () => void) {
            /* tslint:disable-next-line:ban-native-functions */
            id = window.setTimeout(c, timeoutMS);
        },
        function () {
            if (id) {
                /* tslint:disable-next-line:ban-native-functions */
                window.clearTimeout(id);
            }
        }
    );
}

function timeoutWithPromise<T>(timeout: ICancelablePromise<void>, promise: ICancelablePromise<T>) {
    let cancelPromise = function () { promise.cancel(); };
    let cancelTimeout = function () { timeout.cancel(); };
    timeout.then(cancelPromise);
    promise.then(cancelTimeout, cancelTimeout);
    return promise;
}

function getCancelAll<T>(values: { [key: string]: T | IThenable<T> } | (T | IThenable<T>)[]): () => void {
    return () => {
        for (const key of Object.keys(values)) {
            let promise = CancelablePromiseImplementation.as(values[key]);
            promise.cancel();
        }
    };
}

let staticCanceledPromise;

export type MaybePromisified<T> = {[P in keyof T]: T[P] | IThenable<T[P]> | ICancelablePromise<T[P]> };

class CancelablePromiseImplementation<T> implements ICancelablePromise<T> {
    public _listeners: IListener<T, any>[];
    public _nextState: IState;
    public _state: IState;
    public _value: T | IThenable<T> | ICancelablePromise<T>;
    public _oncancel: () => void;
    public _isException: boolean;
    public _errorId: number;

    public static get cancel(): ICancelablePromise<any> {
        return staticCanceledPromise || (staticCanceledPromise = new ErrorPromise(new Error(canceledName)));
    }

    constructor(init?: (c: Resolve<T>, e: Reject) => any, oncancel?: OnCancel) {
        this._init(init, oncancel);
    }

    /**
     * Returns a promise that is fulfilled when one of the input promises
     * has been fulfilled.
     */
    public static any<T>(values: (T | IThenable<T>)[] | { [key: string]: T | IThenable<T> }): ICancelablePromise<{ key: string; value: ICancelablePromise<T>; }> {
        return new CancelablePromiseImplementation(
            function (complete: Resolve<{
                key: string;
                value: ICancelablePromise<T>;
            }>, error: OnError<any>) {
                const keys = Object.keys(values);
                let pending = keys.length;
                if (pending === 0) {
                    complete();
                }
                for (const key of keys) {
                    CancelablePromiseImplementation.as(values[key]).then(
                        function () { complete({ key: key, value: values[key] }); },
                        function (e: any) {
                            if (e instanceof Error && e.name === canceledName) {
                                if (--pending === 0) {
                                    complete(CancelablePromiseImplementation.cancel);
                                }
                                return;
                            }
                            error({ key: key, value: values[key] });
                        }
                    );
                }
            },
            getCancelAll(values)
        );
    }

    public static race<T>(values: (T | IThenable<T>)[]): ICancelablePromise<T> {
        return new CancelablePromiseImplementation(
            function (complete: Resolve<T>, error: OnError<any>) {
                const keys = Object.keys(values);
                let pending = keys.length;
                if (pending === 0) {
                    complete();
                }
                for (const key of keys) {
                    CancelablePromiseImplementation.as(values[key]).then(
                        complete,
                        function (e: any) {
                            if (e instanceof Error && e.name === canceledName) {
                                if (--pending === 0) {
                                    complete(CancelablePromiseImplementation.cancel);
                                }
                                return;
                            }
                            error(e);
                        }
                    );
                }
            },
            getCancelAll(values)
        );
    }

    /**
     * Returns a promise. If the object is already a promise it is returned;
     * otherwise the object is wrapped in a promise.
     */
    public static as<T>(value?: T | IThenable<T> | ICancelablePromise<T>): ICancelablePromise<T> {
        return CancelablePromiseImplementation.is(value) ? value : new CompletePromise(value);
    }

    /**
     * Determines whether a value fulfills the promise contract.
     */
    public static is<T>(value: T | IThenable<T> | ICancelablePromise<T>): value is CancelablePromiseImplementation<T> {
        return CancelablePromiseImplementation.isPromise(value) && typeof (<ICancelablePromise<T>>value).cancel === 'function';
    }

    public static isPromise<T>(value: void | T | IThenable<T>): value is IThenable<T> {
        return value && typeof value === "object" && typeof (<IThenable<T>>value).then === "function";
    }

    /**
     * Determines whether an error value represents a promise cancellation.
     */
    public static isCanceled(e: {} | Error): e is Error {
        return (e instanceof Error && e.name === canceledName);
    }

    /**
     * Creates a promise that is fulfilled when all the values are fulfilled.
     */
    public static all<T>(values: (T | IThenable<T> | ICancelablePromise<T>)[]): ICancelablePromise<T[]>;
    public static all<T>(values: MaybePromisified<T>): ICancelablePromise<T>;
    public static all(values: any): ICancelablePromise<any> {
        return new CancelablePromiseImplementation(
            function (complete: any, error: any) {
                const keys = Object.keys(values);
                const errors = Array.isArray(values) ? [] : {};
                const results = Array.isArray(values) ? [] : {};
                let pending = keys.length;
                const argDone = () => {
                    if ((--pending) === 0) {
                        const errorKeys = Object.keys(errors);
                        let errorCount = errorKeys.length;
                        if (errorCount === 0) {
                            complete(results);
                        } else {
                            if (errorKeys.every((key: string) => CancelablePromiseImplementation.isCanceled(errors[key]))) {
                                complete(CancelablePromiseImplementation.cancel);
                            } else {
                                error(errors);
                            }
                        }
                    }
                };
                for (const key of keys) {
                    const value = values[key];
                    if (value === undefined) {
                        pending--;
                    } else {
                        CancelablePromiseImplementation.then(value,
                            (result: any) => { results[key] = result; argDone(); },
                            (error: any) => { errors[key] = error; argDone(); }
                        );
                    }
                }
                if (pending === 0) {
                    complete(results);
                }
            },
            getCancelAll(values)
        );
    }

    public static then<T, U>(value: T | IThenable<T> | ICancelablePromise<T>, onComplete: OnComplete<T, U>, onError: OnError<U>): ICancelablePromise<U> {
        return CancelablePromiseImplementation.as(value).then(onComplete, onError);
    }

    public static thenEach<T, U>(
        values: ICancelablePromise<T>[],
        onComplete: OnComplete<T, U>,
        onError?: OnError<U>): ICancelablePromise<U[]>;
    public static thenEach<T, U>(
        values: { [key: string]: ICancelablePromise<T> },
        onComplete: OnComplete<T, U>,
        onError?: OnError<U>): ICancelablePromise<{ [keys: string]: U }>;
    public static thenEach<T, U>(
        values: { [key: string]: ICancelablePromise<T> } | ICancelablePromise<T>[],
        onComplete: OnComplete<T, U>,
        onError?: OnError<U>): ICancelablePromise<U[]> | ICancelablePromise<{ [keys: string]: U }> {
        const result: any = Array.isArray(values) ? [] : {};
        for (const key of Object.keys(values)) {
            result[key] = CancelablePromiseImplementation.then(values[key], onComplete, onError);
        }
        return CancelablePromiseImplementation.all(result);
    }

    /**
     * Execute a series of functions that returns a promise, in order.
     * @param tasks An array of functions that return a promise.
     * @returns A promise that is complete when all tasks are complete.
     */
    public static serial<T>(tasks: (() => (IThenable<T>))[]): ICancelablePromise<T>;
    public static serial(tasks: (() => (IThenable<any>))[]): ICancelablePromise<any> {
        return tasks.reduce((previous: ICancelablePromise<any>, task: () => ICancelablePromise<any>) => {
            return previous.then(task);
        }, CancelablePromiseImplementation.wrap());
    }

    /**
     * Creates a promise that is fulfilled after a timeout.
     */
    public static timeout(time: number): ICancelablePromise<void>;
    public static timeout<T>(time: number, promise: ICancelablePromise<T>): ICancelablePromise<T>;
    public static timeout<T>(time: number, promise?: ICancelablePromise<T>): ICancelablePromise<T | void> {
        const to = timeout(time);
        return promise ? timeoutWithPromise(to, promise) : to;
    }

    /**
     * Wraps a non-promise value in a promise. You can use this function if you need
     * to pass a value to a function that requires a promise.
     */
    public static wrap<T>(value?: T | IThenable<T> | ICancelablePromise<T>): ICancelablePromise<T> {
        return new CompletePromise(value);
    }

    public static resolve<T>(value?: T | IThenable<T> | ICancelablePromise<T>): ICancelablePromise<T> {
        return CancelablePromiseImplementation.wrap(value);
    }

    /**
     * Wraps a non-promise error value in a promise. You can use this function if you need
     * to pass an error to a function that requires a promise.
     */
    public static wrapError(error?: any): ICancelablePromise<any> {
        return new ErrorPromise(error);
    }

    public static reject(error?: any): ICancelablePromise<never> {
        return <ICancelablePromise<never>>CancelablePromiseImplementation.wrapError(error);
    }
    /**
     * Attempts to cancel the fulfillment of a promised value. If the promise hasn't
     * already been fulfilled and cancellation is supported, the promise enters
     * the error state with a value of Error("Canceled").
     */
    public cancel() {
        this._state.cancel(this);
        this._run();
    }

    /**
     * Allows you to specify the work to be done on the fulfillment of the promised value,
     * the error handling to be performed if the promise fails to fulfill
     * a value.
     *
     * After the handlers have finished executing, this function throws any error that would have been returned
     * from then() as a promise in the error state.
     *
     * @param onComplete The function to be called if the promise is fulfilled successfully with a value. The fulfilled value is passed as the single argument. If the value is null, the fulfilled value is returned. The value returned from the function becomes the fulfilled value of the promise returned by then. If an exception is thrown while executing the function, the promise returned by then moves into the error state.
     * @param onError The function to be called if the promise is fulfilled with an error. The error is passed as the single argument. If it is null, the error is forwarded. The value returned from the function is the fulfilled value of the promise returned by then.
     */
    public done(onComplete?: (value: T) => any, onError?: (error: any) => any): void {
        this._state.done(this, onComplete, onError);
    }

    /**
     * Allows you to specify the work to be done on the fulfillment of the promised value,
     * the error handling to be performed if the promise fails to fulfill
     * a value.
     *
     * @param onComplete The function to be called if the promise is fulfilled successfully with a value. The value is passed as the single argument. If the value is null, the value is returned. The value returned from the function becomes the fulfilled value of the promise returned by then. If an exception is thrown while this function is being executed, the promise returned by then moves into the error state.
     * @param onError The function to be called if the promise is fulfilled with an error. The error is passed as the single argument. In different cases this object may be of different types, so it is necessary to test the object for the properties you expect. If the error is null, it is forwarded. The value returned from the function becomes the value of the promise returned by the then function.
     * @returns The promise whose value is the result of executing the complete or error function.
     */
    public then<U>(onComplete?: OnComplete<T, U>, onError?: OnError<U>): ICancelablePromise<U>;
    public then<U, V>(onComplete?: OnComplete<T, U>, onError?: OnError<V>): ICancelablePromise<U>;
    public then<U>(onComplete?: OnComplete<T, U>, onError?: OnError<U>): ICancelablePromise<U> {
        return this._state.then(this, onComplete, onError);
    }

    public catch<U>(onError?: OnError<U>): ICancelablePromise<U> {
        return this.then(undefined, onError);
    }

    public _init(init?: (c: Resolve<T>, e: Reject) => any, oncancel?: OnCancel) {
        this._oncancel = oncancel;
        this._setState(state_created);
        this._run();

        try {
            init(this._completed.bind(this), this._error.bind(this));
        } catch (ex) {
            this._setExceptionValue(ex);
        }
    }

    public _cancelAction() {
        if (this._oncancel) {
            try { this._oncancel(); } catch (ex) { /* ignore */ }
        }
    }

    public _cleanupAction() {
        this._oncancel = null;
    }

    public _chainedError(value: any, context: IErrorContext) {
        const result = this._state._error(this, value, detailsForChainedError, context);
        this._run();
        return result;
    }

    public _completed(value: T | IThenable<T> | ICancelablePromise<T>) {
        const result = this._state._completed(this, value);
        this._run();
        return result;
    }

    public _error(value: {} | IThenable<{}> | ICancelablePromise<{}>) {
        const result = this._state._error(this, value, detailsForError);
        this._run();
        return result;
    }

    public _setState(state: any) {
        this._nextState = state;
    }

    public _setCompleteValue(value: any) {
        this._state._setCompleteValue(this, value);
        this._run();
    }

    public _setChainedErrorValue(value: any, context: IErrorContext) {
        const result = this._state._setErrorValue(this, value, detailsForChainedError, context);
        this._run();
        return result;
    }

    public _setExceptionValue(value: any) {
        const result = this._state._setErrorValue(this, value, detailsForException);
        this._run();
        return result;
    }

    public _run() {
        while (this._nextState) {
            this._state = this._nextState;
            this._nextState = null;
            this._state.enter(this);
        }
    }

    public _isSameValue<U>(value: T | IThenable<T> | ICancelablePromise<T> | U | IThenable<U> | ICancelablePromise<U>): this is ICancelablePromise<U> {
        return value === this._value;
    }
}

Events.declare(CancelablePromiseImplementation, errorET);

//
// Internal implementation detail promise, ThenPromise is created when a promise needs
// to be returned from a then() method.
//
class ThenPromise<T> extends CancelablePromiseImplementation<T> {
    private _creator: ICancelablePromise<any>;

    constructor(creator: ICancelablePromise<any>) {
        super();

        this._creator = creator;

        this._initThen();
    }

    public _init() {
        // Override to do nothing.
    }

    public _cancelAction() {
        if (this._creator) { this._creator.cancel(); }
    }

    public _cleanupAction() {
        this._creator = null;
    }

    private _initThen() {
        this._setState(state_created);
        this._run();
    }
}

/**
 * Legacy interface for the `SignalPromise` export to provide the implementation of the `Signal` module.
 * Do not use outside this source package.
 *
 * @export
 * @interface ISignalPromise
 * @extends {ICancelablePromise<T>}
 * @template T
 */
export declare interface ISignalPromise<T> extends ICancelablePromise<T> {
    _completed(value?: T): void;
    _error(value?: any): void;
}

/**
 * Extension of the `ICancelablePromiseConstructor` type which provides
 * the constructor for a 'signal' promise.
 */
export type ISignalPromiseConstructor = {
    new <T>(cancel: OnCancel): ISignalPromise<T>;
} & {
        [P in keyof ICancelablePromiseConstructor]: ICancelablePromiseConstructor[P];
    };

class SignalPromiseImplementation<T> extends CancelablePromiseImplementation<T> {
    constructor(cancel: OnCancel) {
        super();

        this._oncancel = cancel;

        this._initSignal();
    }

    public _init() {
        // Override to do nothing.
    }

    public _cancelAction() {
        if (this._oncancel) {
            this._oncancel();
        }
    }

    public _cleanupAction() {
        this._oncancel = null;
    }

    private _initSignal() {
        this._setState(state_created);
        this._run();
    }
}

/**
 * Represents a singal promise type and constructor.
 * This type is for legacy use only, and should not be used outside this source package.
 */
export const SignalPromise: ISignalPromiseConstructor = SignalPromiseImplementation;
export type SignalPromise<T> = ISignalPromise<T>;

/**
 * Represents a cancelable promise type and constructor.
 * This type is intended to be assignable to the ES6 `Promise` type for compatibility,
 * so that `CancelablePromise` may be a used as a polyfill for `Promise`.
 */
export const CancelablePromise: ICancelablePromiseConstructor = CancelablePromiseImplementation;
export type CancelablePromise<T> = ICancelablePromise<T>;

export default CancelablePromise;
